/**
 * AddAccountModal Component
 *
 * Modal for adding a new account with initial balance support.
 * Creates an adjustment transaction for non-zero initial balances.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Button, Input, Select } from '@/components/shared';
import { accountService, AccountInput } from '@services/accounts';
import { transactionService } from '@services/transactions';
import { db, Account } from '@services/db';
import { useLanguage } from '@/i18n';
import './AddAccountModal.css';

interface AddAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type AccountType = 'checking' | 'savings' | 'cash' | 'credit' | 'crypto' | 'investment';

interface FormState {
  name: string;
  fullName: string;
  type: AccountType;
  currency: string;
  initialBalance: string;
}

const initialFormState: FormState = {
  name: '',
  fullName: '',
  type: 'checking',
  currency: 'USD',
  initialBalance: '',
};

// Stored enum values (machine codes); display labels come from the catalog
// (accounts.type.* / currencies.*) at render time - never store translations.
const ACCOUNT_TYPES: AccountType[] = ['checking', 'savings', 'cash', 'credit', 'crypto', 'investment'];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'THB', 'JPY', 'CAD', 'AUD'];

// One-tap type chips (icons are UI decoration; labels stay catalog-driven)
const TYPE_ICONS: Record<AccountType, string> = {
  checking: '💳',
  savings: '🏦',
  cash: '💵',
  credit: '🧾',
  crypto: '🪙',
  investment: '📈',
};

export function AddAccountModal({
  isOpen,
  onClose,
  onSuccess,
}: AddAccountModalProps) {
  const { t } = useLanguage();
  const [form, setForm] = useState<FormState>(initialFormState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens + load existing accounts for the
  // duplicate-name warning (includes hidden accounts on purpose)
  const [existingAccounts, setExistingAccounts] = useState<Account[]>([]);
  useEffect(() => {
    if (isOpen) {
      setForm(initialFormState);
      setError(null);
      db.accounts
        .toArray()
        .then(setExistingAccounts)
        .catch(() => setExistingAccounts([]));
    }
  }, [isOpen]);

  // Non-blocking duplicate-name warning (duplicates confuse transfers and
  // the account dropdowns, but the user may still want one)
  const duplicateName = useMemo(() => {
    const name = form.name.trim().toLowerCase();
    if (!name) return null;
    const match = existingAccounts.find((a) => a.name.trim().toLowerCase() === name);
    return match ? match.name : null;
  }, [form.name, existingAccounts]);

  // Handle form field change
  const handleChange = useCallback(
    (field: keyof FormState, value: string) => {
      setForm((f) => ({ ...f, [field]: value }));
      setError(null);
    },
    []
  );

  // Validate form
  const validate = useCallback((): string | null => {
    if (!form.name.trim()) {
      return t('accounts.actions.err_name_required');
    }
    if (form.name.trim().length > 20) {
      return t('accounts.add.err_name_long');
    }
    if (form.fullName.trim().length > 50) {
      return t('accounts.add.err_full_long');
    }
    // Validate initial balance if provided
    if (form.initialBalance.trim()) {
      const balance = parseFloat(form.initialBalance);
      if (isNaN(balance)) {
        return t('accounts.add.err_balance_invalid');
      }
    }
    return null;
  }, [form, t]);

  // Handle submit
  const handleSubmit = useCallback(async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const initialBalance = form.initialBalance.trim()
        ? parseFloat(form.initialBalance)
        : 0;

      // Create the account
      const accountData: AccountInput = {
        name: form.name.trim(),
        fullName: form.fullName.trim() || undefined,
        type: form.type,
        currency: form.currency,
        isActive: true,
      };

      const newAccountId = await accountService.create(accountData);
      console.log('[AddAccount] Created account:', newAccountId, form.name);

      // If initial balance is non-zero, create an adjustment transaction
      // This properly sets the starting balance via the transaction system
      if (initialBalance !== 0) {
        await transactionService.create({
          accountId: newAccountId,
          type: initialBalance > 0 ? 'income' : 'expense',
          amount: Math.abs(initialBalance),
          description: '[Adjustment] Initial balance',
          date: new Date(),
          currency: form.currency,
        });
        console.log('[AddAccount] Created initial balance adjustment:', initialBalance);
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('accounts.add.err_create_failed');
      setError(message);
      console.error('[AddAccount] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [form, validate, onSuccess, onClose, t]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('accounts.add.title')} size="md">
      <div className="add-account-form">
        {/* Account Name */}
        <Input
          label={t('accounts.add.name_label')}
          value={form.name}
          onChange={(e) => handleChange('name', e.target.value)}
          placeholder={t('accounts.add.name_placeholder')}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit();
            if (e.key === 'Escape') onClose();
          }}
        />
        {duplicateName && (
          <div className="dup-warning">
            {t('accounts.add.dup_warning', { name: duplicateName })}
          </div>
        )}

        {/* Full Name (optional) */}
        <Input
          label={t('accounts.actions.full_name')}
          value={form.fullName}
          onChange={(e) => handleChange('fullName', e.target.value)}
          placeholder={t('accounts.actions.full_name_placeholder')}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit();
            if (e.key === 'Escape') onClose();
          }}
        />

        {/* Account Type - one-tap chips (was a dropdown) */}
        <div className="type-chip-group">
          <span className="type-chip-label">{t('accounts.add.type_label')}</span>
          <div className="type-chip-grid">
            {ACCOUNT_TYPES.map((value) => (
              <button
                key={value}
                type="button"
                className={`type-chip ${form.type === value ? 'active' : ''}`}
                onClick={() => handleChange('type', value)}
              >
                <span className="type-chip-icon">{TYPE_ICONS[value]}</span>
                <span>{t(`accounts.type.${value}`)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Currency */}
        <Select
          label={t('accounts.add.currency_label')}
          value={form.currency}
          onChange={(e) => handleChange('currency', e.target.value)}
          options={CURRENCIES.map((value) => ({ value, label: t(`currencies.${value}`) }))}
        />

        {/* Initial Balance */}
        <Input
          type="number"
          label={t('accounts.add.initial_label')}
          value={form.initialBalance}
          onChange={(e) => handleChange('initialBalance', e.target.value)}
          placeholder="0.00"
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit();
            if (e.key === 'Escape') onClose();
          }}
        />

        <div className="balance-hint">
          {t('accounts.add.balance_hint')}
        </div>

        {/* Error Message */}
        {error && <div className="error-message">{error}</div>}

        {/* Action Buttons */}
        <div className="form-buttons">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? t('accounts.add.creating') : t('accounts.add.submit')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
